import {Injectable} from '@angular/core';
import {LoanApplication} from '../model/loan-application.model';
import {HttpClient} from '@angular/common/http';
import {ConfigService} from './config.service';
import {LoanApplicationTransition} from '../model/LoanApplicationTransition';
import {LoanApplications} from '../model/loan-applications.model';
import {Tasks} from '../model/task.mode';
import {LoanApplicationCloningRequest} from '../model/loan-application/loan-application-cloning-request.model';
import {MultipleLoanEligibilityCheckResponse} from '../model/eligibility-check/multiple-loan-eligibility-check.model';
import {LoanApplicationSource} from '../model/loan-application/loan-application-source.model';

@Injectable()
export class LoanApplicationService {
    private readonly httpClient: HttpClient;
    private readonly configService: ConfigService;

    constructor(httpClient: HttpClient, configService: ConfigService) {
        this.httpClient = httpClient;
        this.configService = configService;
    }

    public async createLoanApplication(loanApplication: LoanApplication): Promise<LoanApplication> {
        // TODO - Ashray/Siva - Build fault tolerance around url creation
        const url = this.configService.loanApplicationServiceUrl() + '/loan-application';
        return this.httpClient.post<LoanApplication>(url, loanApplication).toPromise();
    }

    async updateLoanApplication(application: LoanApplication): Promise<void> {
        const url = `${this.configService.loanApplicationServiceUrl()}/loan-application/${application.id}`;
        return this.httpClient.put<void>(url, application).toPromise();
    }

    async getLoanApplication(id: string): Promise<LoanApplication> {
        const url = `${this.configService.loanApplicationServiceUrl()}/loan-application/${id}`;
        return this.httpClient.get<LoanApplication>(url).toPromise();
    }

    async getLoanApplicationsByUserId(userId: string): Promise<LoanApplications> {
        const url = `${this.configService.loanApplicationServiceUrl()}/loan-applications?userId=${userId}`;
        return this.httpClient.get<LoanApplications>(url).toPromise();
    }
    async getLoanApplicationsByApplicationIds(loanApplicationIds: string[]): Promise<LoanApplications> {
        const url = `${this.configService.loanApplicationServiceUrl()}/loan-applications`;
        return this.httpClient.post<LoanApplications>(url, loanApplicationIds).toPromise();
    }

    async transitApplication(loanApplicationId: string, applicationTransition: LoanApplicationTransition): Promise<LoanApplicationTransition> {
        const url = `${this.configService.loanApplicationServiceUrl()}/loan-application/${loanApplicationId}/transition`;
        return this.httpClient.post<LoanApplicationTransition>(url, applicationTransition).toPromise();
    }

    async getLatestTransition(loanApplicationId: string): Promise<LoanApplicationTransition> {
        const url = `${this.configService.loanApplicationServiceUrl()}/loan-application/${loanApplicationId}/transition`;
        return this.httpClient.get<LoanApplicationTransition>(url).toPromise();
    }

    async getTasksForApplication(loanApplicationId: string): Promise<Tasks> {
        const baseUrl = `${this.configService.loanApplicationServiceUrl()}/loan-application/${loanApplicationId}/assessment-tasks`;
        return this.httpClient.get<Tasks>(baseUrl).toPromise();
    }

    async canReapplyForLoan(userId: string): Promise<boolean> {
        const baseUrl = `${this.configService.loanApplicationServiceUrl()}/loan-application/can-reapply?userId=${userId}`;
        return this.httpClient.get<boolean>(baseUrl).toPromise();
    }

    async getMultipleLoanEligibilityForUser(userId: string, source?: LoanApplicationSource): Promise<MultipleLoanEligibilityCheckResponse> {
        const baseUrl = source ? `${this.configService.loanApplicationServiceUrl()}/loan-application/multiple-loan-eligibility-check?userId=${userId}&source=${source}` :
            `${this.configService.loanApplicationServiceUrl()}/loan-application/multiple-loan-eligibility-check?userId=${userId}`;
        return this.httpClient.get<MultipleLoanEligibilityCheckResponse>(baseUrl).toPromise();
    }

    async getAllTransitions(loanApplicationId: string): Promise<LoanApplicationTransition[]> {
        const url = `${this.configService.loanApplicationServiceUrl()}/loan-application/${loanApplicationId}/transitions`;
        return this.httpClient.get<LoanApplicationTransition[]>(url).toPromise();
    }

    async getAllTransitionsByLoanApplicationIds(loanApplicationIds: string[]): Promise<LoanApplicationTransition[]> {
        const url = `${this.configService.loanApplicationServiceUrl()}/loan-application-transitions`;
        return this.httpClient.post<LoanApplicationTransition[]>(url, loanApplicationIds).toPromise();
    }

    async cloneLoanApplication(loanApplicationCloningRequest: LoanApplicationCloningRequest): Promise<LoanApplicationCloningRequest> {
        const url = `${this.configService.loanApplicationServiceUrl()}/loan-application/clone`;
        return this.httpClient.post<LoanApplicationCloningRequest>(url, loanApplicationCloningRequest).toPromise();
    }

    async getLoanApplicationCloningRequestByClonedLoanApplicationId(clonedLoanApplicationId: string): Promise<LoanApplicationCloningRequest> {
        const url = `${this.configService.loanApplicationServiceUrl()}/loan-application/clone?clonedLoanApplicationId=${clonedLoanApplicationId}`;
        return this.httpClient.get<LoanApplicationCloningRequest>(url).toPromise();
    }
}
